// @dx ds/domain — the factory-bound static-tag catalog.
//
// Every export here is produced by `createStaticTag` / `createPlainTag` from
// `@ds/domain/EnumTag.factories`. Bespoke tags that need extra chrome live in
// `@ds/domain/EnumTag.Composites`.
//
// Every visual value comes from `@ds/tokens/palette` via the Tag primitive.
// No hex codes, no inline colors.
import {
  DocumentStatus, TransactionStatus, GDStatus,
  PriceType, PriceListSide, InvoiceType,
  InvoiceDirection, InvoiceSource, InvoiceReviewStatus, InvoicePaymentStatus,
  InvoiceSyncStatus, BorderType, PaymentMethod, type InvoicedStatus,
} from "@shared/types";
import {
  DOCUMENT_STATUS_COLORS,
  TRANSACTION_STATUS_COLORS, GD_STATUS_COLORS, PRICE_TYPE_COLORS,
  INVOICE_TYPE_COLORS, INVOICE_DIRECTION_COLORS, INVOICE_SOURCE_COLORS,
  INVOICE_REVIEW_STATUS_COLORS, INVOICE_PAYMENT_STATUS_COLORS,
  INVOICE_SYNC_STATUS_COLORS, INVOICED_STATUS_COLORS, BORDER_TYPE_COLORS,
  BANK_BATCH_STATUS_COLORS, BANK_PAYMENT_STATUS_COLORS,
  BANK_LINE_MATCH_STATE_COLORS, INVOICE_PAYMENT_METHOD_COLORS,
  PRICE_LIST_SIDE_COLORS,
  RECONCILE_SIDE_COLORS,
} from "@ds/tokens/domainTones";
import type {
  MbankOutboundBatchStatus, MbankOutboundPaymentStatus,
  MbankStatementLineMatchState,
} from "@shared/types/mbank";
import { EmptyValue } from "@ds/display/EmptyValue";
import { createStaticTag, createPlainTag } from "@ds/domain/EnumTag.factories";

// ============================================
// Document Tags
// ============================================

export const DocumentStatusTag = createStaticTag<DocumentStatus>(DOCUMENT_STATUS_COLORS, "documents.document_status");

// ============================================
// Transaction Tags
// ============================================

export const TransactionStatusTag = createStaticTag<TransactionStatus>(TRANSACTION_STATUS_COLORS, "transactions.status");
export const BorderTypeTag = createStaticTag<BorderType>(BORDER_TYPE_COLORS, "transactions.border_type");
export const GDStatusTag = createStaticTag<GDStatus>(GD_STATUS_COLORS, "transactions.gd_status", <></>);

// ============================================
// Pricing Tags
// ============================================

export const PriceTypeTag = createStaticTag<PriceType>(PRICE_TYPE_COLORS, "pricings.price_type");

// Uses PRICE_LIST_SIDE_COLORS (load→blue, unload→green), distinct from SIDE_COLORS
// (load→blue, unload→purple) used by SideChip on transactions.
export const PriceListSideTag = createStaticTag<PriceListSide>(PRICE_LIST_SIDE_COLORS, "price_lists.side");

// Three-value reconcile side (load/unload/transport) display.
export const ReconcileSideTag = createStaticTag<"load" | "unload" | "transport">(
  RECONCILE_SIDE_COLORS,
  "price_lists.side",
);

// ============================================
// Common Tags
// ============================================

export const IncotermsTag = createPlainTag("amber");

// ============================================
// Invoice Tags
// ============================================

export const InvoiceTypeTag = createStaticTag<InvoiceType>(INVOICE_TYPE_COLORS, "invoices.invoice_type");
export const InvoiceDirectionTag = createStaticTag<InvoiceDirection>(INVOICE_DIRECTION_COLORS, "invoices.direction");
export const InvoiceSourceTag = createStaticTag<InvoiceSource>(INVOICE_SOURCE_COLORS, "invoices.source");

export const InvoiceReviewStatusTag = createStaticTag<InvoiceReviewStatus>(INVOICE_REVIEW_STATUS_COLORS, "invoices.review_status", <EmptyValue />, true);
export const InvoicePaymentStatusTag = createStaticTag<InvoicePaymentStatus>(INVOICE_PAYMENT_STATUS_COLORS, "invoices.payment_status", <EmptyValue />, true);
export const InvoicePaymentMethodTag = createStaticTag<PaymentMethod>(INVOICE_PAYMENT_METHOD_COLORS, "invoices.payment_method", <EmptyValue />, true);
export const InvoiceSyncStatusTag = createStaticTag<InvoiceSyncStatus>(INVOICE_SYNC_STATUS_COLORS, "invoices.sync_status", <EmptyValue />, true);
export const InvoicedStatusTag = createStaticTag<InvoicedStatus>(INVOICED_STATUS_COLORS, "transactions.invoiced_status", <EmptyValue />, true);

// ============================================
// Bank (mBank) Tags
// ============================================

export const BankBatchStatusTag = createStaticTag<MbankOutboundBatchStatus>(
  BANK_BATCH_STATUS_COLORS,
  "bank.batch.status",
);

export const BankPaymentStatusTag = createStaticTag<MbankOutboundPaymentStatus>(
  BANK_PAYMENT_STATUS_COLORS,
  "bank.item.status_values",
);

export const BankLineMatchStateTag = createStaticTag<MbankStatementLineMatchState>(
  BANK_LINE_MATCH_STATE_COLORS,
  "bank.line.match_state_values",
);
